/**
 * Unity world space ↔ web canvas pixel conversion for LitSquad
 * accessory placement.
 *
 * Unity authors transforms in world units with Y up; the avatar
 * editor draws onto an HTML canvas with Y down. This module converts
 * one to the other using a small set of named, tunable constants.
 *
 * The constants below are derived from the LitLegion avatar render
 * setup (1200×1200 RenderTexture, perspective camera FOV 37°, character
 * body parts spanning roughly y=0 (feet) to y=14 (top of head)). They
 * are exported so calibration tweaks live in one place.
 */

import type { AccessoryTransform } from './accessory-transforms.generated';

/**
 * The reference canvas size the constants below are calibrated for.
 * Other canvas sizes scale linearly off this baseline so the avatar
 * editor's full-size compose (512px) and any future thumbnail render
 * (e.g. 128px mini) stay visually consistent.
 */
export const REFERENCE_CANVAS_SIZE = 512;

/**
 * Pixels per Unity world unit at the reference canvas size.
 *
 * Calibration target: a character that spans ~14 world units
 * vertically (feet at y=0, top of head at y~14) should occupy roughly
 * 75% of the canvas height. 14 * 28 = 392 px on a 512 canvas ≈ 76%.
 *
 * If accessories appear too large or small relative to the base
 * character, adjust this value (and re-test against multiple species
 * — Crab and Snake have different proportions than Bear).
 */
export const PIXELS_PER_UNIT = 28;

/**
 * Where Unity world (0, 0) sits on the canvas, expressed as a fraction
 * of the canvas dimensions:
 *   - x: 0.5 means Unity x=0 maps to canvas horizontal center.
 *   - y: 0.85 means Unity y=0 (feet) maps to 85% down the canvas
 *     (i.e. a small margin above the bottom edge).
 */
export const ORIGIN_FRACTION = { x: 0.5, y: 0.85 } as const;

/**
 * Unity sprite "Pixels Per Unit" — the rate at which a sprite's
 * source-pixel dimensions translate to world units. 100 is the
 * Unity engine default and matches the LitLegion sprite metadata
 * (`spritePixelsToUnits: 100`).
 *
 * A 1024×1024 source image at SPRITE_PPU=100 is 10.24 world units
 * wide at scale=1 — drawn through {@link unityToCanvas}, that becomes
 * 10.24 * PIXELS_PER_UNIT ≈ 287 px on a 512 canvas.
 */
export const SPRITE_PPU = 100;

/**
 * Converts a Unity-local world position to a canvas pixel coordinate
 * for the given canvas size.
 */
export function unityToCanvas(
  worldX: number,
  worldY: number,
  canvasSize: number,
): { x: number; y: number } {
  const scale = canvasSize / REFERENCE_CANVAS_SIZE;
  const ppu = PIXELS_PER_UNIT * scale;
  const x = canvasSize * ORIGIN_FRACTION.x + worldX * ppu;
  // Unity Y is up, canvas Y is down — invert.
  const y = canvasSize * ORIGIN_FRACTION.y - worldY * ppu;
  return { x, y };
}

/**
 * Computes the canvas-space draw size of a sprite given its natural
 * source-image dimensions and the Unity scale on the transform.
 */
export function spriteCanvasSize(
  imageWidth: number,
  imageHeight: number,
  transform: AccessoryTransform,
  canvasSize: number,
): { width: number; height: number } {
  const ppu = PIXELS_PER_UNIT * (canvasSize / REFERENCE_CANVAS_SIZE);
  const widthUnits = imageWidth / SPRITE_PPU;
  const heightUnits = imageHeight / SPRITE_PPU;
  return {
    width: widthUnits * ppu * transform.scale.x,
    height: heightUnits * ppu * transform.scale.y,
  };
}

/**
 * Draws an accessory image onto a 2D canvas context with the per-
 * character transform applied:
 *   1. Translate to the world Position mapped through unityToCanvas.
 *   2. Rotate around that point. Unity rotates CCW for positive Z
 *      with Y-up; canvas rotates CW for positive radians with Y-down.
 *      The two flips cancel and we negate once to get the right
 *      visual sense.
 *   3. Scale by the per-axis scale factor.
 *   4. Draw the image centered on the rotation/scale origin — i.e.
 *      the source image's pixel center is treated as the sprite
 *      pivot. This matches the LitSquad accessory thumbnails which
 *      are 1024×1024 PNG/JPG composites with the accessory drawn
 *      around the image centerpoint.
 *
 * Always wrapped in `save`/`restore` so the caller's transform stack
 * is untouched.
 */
export function drawAccessoryWithTransform(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  imageWidth: number,
  imageHeight: number,
  transform: AccessoryTransform,
  canvasSize: number,
): void {
  const { x: cx, y: cy } = unityToCanvas(transform.position.x, transform.position.y, canvasSize);
  const { width, height } = spriteCanvasSize(imageWidth, imageHeight, transform, canvasSize);

  ctx.save();
  ctx.translate(cx, cy);
  // Unity Z rotation is CCW around the screen normal in a Y-up frame.
  // Canvas rotate(θ) is CW when Y points down, so we negate.
  ctx.rotate(-transform.rotationRadians);
  ctx.drawImage(image, -width / 2, -height / 2, width, height);
  ctx.restore();
}

/**
 * Returns the natural pixel size of a CanvasImageSource. Both
 * HTMLImageElement (`naturalWidth`/`naturalHeight`) and HTMLCanvasElement
 * (`width`/`height`) are common inputs to `drawImage`; this normalizes
 * across both.
 *
 * Returns `null` for sources with no measurable size (zero-dimension
 * canvases, images that haven't loaded yet) so callers can fall back
 * to the legacy stretch-to-fill render.
 */
export function getSourceImageSize(
  source: CanvasImageSource,
): { width: number; height: number } | null {
  if (source instanceof HTMLImageElement) {
    if (!source.naturalWidth || !source.naturalHeight) return null;
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  if (source instanceof HTMLCanvasElement) {
    if (!source.width || !source.height) return null;
    return { width: source.width, height: source.height };
  }
  // Other CanvasImageSource shapes (ImageBitmap, OffscreenCanvas,
  // SVGImageElement, VideoFrame) all expose a numeric width/height.
  // Read defensively so a missing/zero size falls through to the
  // stretch-to-fill render rather than throwing.
  const anySrc = source as { width?: unknown; height?: unknown };
  const w = typeof anySrc.width === 'number' ? anySrc.width : 0;
  const h = typeof anySrc.height === 'number' ? anySrc.height : 0;
  if (!w || !h) return null;
  return { width: w, height: h };
}
